import { location, reactive } from 'cdui-js';
import { createNewApply, fetchAllInfos, saveApply } from '../../../../api/open-account';
import { aesDecryptHexByPass } from '../../../../lib/crypto';
import { commonState, waitForEncryptKey } from '../../../../stores/common';
import { userState, isH5User } from '../../../../stores/user';
import { initBusConfigs } from '../../../../stores/bus-config';
import { consumeApplySeed, waitForApplySeed } from '../../../../stores/apply-seed';
import {
  initRNBridge,
  postToApp,
  registerRNHandler,
} from '../../../../lib/rnBridge';
import { OpenAccountBootstrapTimeoutError, persistMainlandOpenAccountToken, waitForOpenAccountBootstrap } from './openAccountSession';
import dayts from '@china-difi/lib/date';
import { applyOpinionErrors, parseOpinionPayload } from './errorKeyNormalize';
import i18n from '../../../../i18n';
import { notifyKycStepChange, syncKycStepView } from './kycAnalytics';
import { clearSzcaCertCache, clearSzcaPendingState } from './on-line/risk/szca-face';
import { isSzcaReturnFromQuery } from './on-line/risk/openSzcaAuthViaApp';
import { preloadDeepLinkShell, preloadEditRefusedSteps } from './idlePreload';
import { STEPS_MAINLAND, STEPS_NON_MAINLAND } from './on-line/stepMaps';
import { routeBackFromOpenAccountDocs } from './openAccountDocsNav';
import { getTransferIntroStep, isFinanceTransferEditRole } from './editRefusedRole';
import { readNameParts } from '../nameParts';
import { showToast } from '../../components/Toast';
import { sanitizeEmploymentDetailFields } from './on-line/infomations/jobStatusEmployment';

const isMainlandIdCard = () => pState.uploadLicForm?.idCardType === 'MAINLAND_CHINA';

// SUBMITTED("SUBMITTED", "Submitted", "已提交", "已提交"),
// APPROVING("APPROVING", "Approving", "审批中", "审批中"),
// REFUSED("REFUSED", "Refused", "被拒绝", "被拒絕"),
// BACK("BACK", "Back", "被退回", "被退回"),
// FINISHED("FINISHED", "Finished", "已结束", "已結束"),
// CANCELLED("CANCELLED", "Cancelled", "已取消", "已取消")

// ── 开户进度缓存（localStorage）──────────────────────────────────────────────
// key 以用户 ID 区分，防止多账号污染；value 为步骤号字符串

/**
 * 大陆身份证流程（CompMaps）中 RiskInfo 所在步骤号。
 * 大陆用户最多只恢复到此步（需重新申请证书），超出部分截断为此值。
 */
const MAINLAND_MAX_RESUME_STEP = 13;

/** 已完结的申请状态，不需要也不应该恢复进度 */
const TERMINAL_STATUSES = [
  'SUBMITTED',
  'APPROVING',
  'INITIAL_REVIEWING',
  'REVIEWING',
  'APPROVED',
  'BACK',
  'FINISHED',
  'CANCELLED',
];

const getProgressCacheKey = () => `open_progress_step_${userState.id || 'anon'}`;

/** v3：大陆 / 非大陆均已去掉活体视频步；旧缓存需迁移 */
const PROGRESS_CACHE_VER = 3;

const saveProgressCache = (step: number) => {
  try {
    if (step > 1) localStorage.setItem(getProgressCacheKey(), `v${PROGRESS_CACHE_VER}:${step}`);
  } catch {}
};

export const clearProgressCache = () => {
  try {
    localStorage.removeItem(getProgressCacheKey());
  } catch {}
};

/** 解析进度缓存；去掉视频步后，旧步号 >13 需 -1 */
const readProgressCache = (isMainland: boolean): number => {
  try {
    const raw = localStorage.getItem(getProgressCacheKey());
    if (!raw) return 0;
    const verMatch = /^v(\d+):(\d+)$/.exec(raw);
    if (verMatch) {
      const ver = parseInt(verMatch[1], 10);
      let step = parseInt(verMatch[2], 10) || 0;
      // v1/v2 大陆含视频步；v2 非大陆已迁移；统一：ver < 3 且 step > 13 则 -1
      if (ver < 3 && step > 13) {
        // 非大陆在 v2 已减过一次，勿重复
        if (!(ver === 2 && !isMainland)) {
          step -= 1;
        }
      }
      return step;
    }
    // legacy：纯数字
    let step = parseInt(raw, 10) || 0;
    if (step > 13) {
      step -= 1;
    }
    return step;
  } catch {
    return 0;
  }
};

let formApi = null;
let formValidateFunc = null;
/** 纠错页当前页名。有值时把 formApi 按页缓存，切回已挂载页时恢复，避免校验落到空或上一页。 */
let formApiOwner = '';
const formApiCache = new Map<string, unknown>();
const formValidateCache = new Map<string, unknown>();

/** 切到纠错页面前恢复该页已注册的校验接口；新页还没有缓存则为空，等表单 ref 再写入。 */
export const restoreReEditFormBindings = (name: string) => {
  formApiOwner = name || '';
  formApi = name ? (formApiCache.get(name) ?? null) : null;
  formValidateFunc = name ? (formValidateCache.get(name) ?? null) : null;
};

export const clearReEditFormBindings = () => {
  formApiOwner = '';
  formApiCache.clear();
  formValidateCache.clear();
};

export const setFormApi = (api) => {
  formApi = api;
  if (api && formApiOwner) formApiCache.set(formApiOwner, api);
};

export const getFormApi = () => formApi;

export const setFormValidateFunc = (api) => {
  formValidateFunc = api;
  if (api && formApiOwner) formValidateCache.set(formApiOwner, api);
};

export const getFormValidateFunc = () => formValidateFunc;

export const pState = reactive({
  fourOrderno: '',
  certP10: '',
  certDn: '',
  keyPair: null,
  certSn: '',
  id: '',
  status: '', // 申请状态（SUBMITTED/INITIAL_REVIEWING/REVIEWING/APPROVED/REJECTED/CANCELLED）
  loading: true,
  /** 深链 / 退回修改：不等 getByUserId 即可挂当前 step 壳，用于首绘与 Ready */
  shellReady: false,
  /**
   * getByUserId / finishInit 完成后才为 true。
   * 业务分支（CheckMethod / OnLine / 现场开户等）必须等此标记，避免默认 applyMethod 闪错页。
   */
  applyReady: false,
  loadError: false,
  loadErrorMessage: '',
  /**
   * 兼容旧字段：与 visitStack 的 step 序列同步（前进 push / 后退 pop）。
   * 禁止业务再手写 `records = [0..n]`；请用 setRecords / jumpVisit / pushVisit。
   */
  records: [0],
  current: 0, // 当前页码（= 栈顶 step）
  applyMethod: 'ONLINE_TRANSFER', // 开户类型
  taxName: '',
  companyAddress: '',
  progress: 0,
  disable: false,

  uploadLicForm: {
    firstNameEn: '',
    lastNameEn: '',
    fullName: '', // 中文名字
    pinyinName: '', // 英文名字 + 英文姓氏
    gender: null, // 性别（默认未选，展示「请选择」）
    identityType: '', // 身份信息：中国大陆居民，香港居民，澳门居民，其它国家/地区居民
    idCardType: 'MAINLAND_CHINA', // 证件类型
    idCardNo: '', // 证件号码
    idCardStartDate: '', // 证件有效期 开始时间
    idCardEndDate: '', // 证件有效期 结束时间
    idCardLongTerm: 0, // 是否长期有效 1:是  0:否
    birthCountry: '', // 出生地
    nationality: '', // 国籍（其它国家/地区）
    birthDate: '',
    idCardIssueAuthority: '', // 签署机关
  },
  unionPayCard: {
    // bankCode: '',
    bankAccountNo: '',
    bankName: '',
    // bankAccountName: '',
    bankCardPurpose: 'UNION_PAY_AUTH', // TRANSFER   UNION_PAY_AUTH
    phoneCode: '+86',
    phone: '',
  },
  addressForm: {
    country: '', // 居住国家
    address: '', // 居住地址(英)
    addressCn: '', // 居住地址(中)；勾选「住址证明是中文」时必填
    correspondenceAddress: '', // 通讯地址详细，仅 addressType=OTHER 时填写
    addressType: '', // 通讯地址类型，单选：RESIDENTIAL / BUSINESS / OTHER
    addressTypeOther: '', // 与 correspondenceAddress 同步，仅 OTHER 时保留
    phoneCountry: '', // 联系电话区号；空值用注册区号 countryCode 带入，已有值不覆盖
    phone: '', // 联系电话；空值用注册手机号带入，已有值不覆盖
    email: '', // 邮箱
  },
  /** Address 页「住址证明是中文」勾选；仅控制显隐，保存未勾选时 addressCn 传空 */
  addressCnChecked: false,
  jobStatusForm: {
    education: '', // 教育背景
    employmentStatus: '', // 受雇状态
    companyName: '', // 公司名称
    industry: '', // 行业
    industryOther: '', // 行业选「其他」时的说明
    position: '', // 职位
    positionOther: '', // 职位选「其他」时的说明
    companyAddress: '', // 公司地址
    employmentYears: '', // 受雇年期
    companyPhone: '', // 公司电话
  },
  financialStatusForm: {
    housingProperty: '', // 物业情况
    annualIncome: '', // 年度收入（港元）
    totalAssetsNetHkd: '', // 流动资产净值（港元）
    wealthSource: '', // 财富来源（可多选，逗号隔开）
    wealthSourceOther: '', // 财富来源选「其他」时的说明
    sourceOfFunds: '', // 资金来源（可多选，逗号隔开）
    sourceOfFundsOther: '', // 资金来源选「其他」时的说明
    fundTransferMethod: '', // 资金转入方式（可多选，逗号隔开）
    sourceOfFundsLocation: '', // 资金来源地
    sourceOfFundsLocationOther: '', // 资金来源地选「其他」时的说明
  },
  investmentExpForm: {
    investmentKnowledge: '', // 投资知识
    investmentExperienceTypes: '', // 投资经验类型（可多选，逗号隔开）
    investmentTarget: '', // 投资目标（可多选，逗号隔开）
    investmentPurposeOther: '', // 投资目标选「其它」时的说明
    stockExperience: '', // 证券年资
    futuresExperience: '', // 期货年资
    optionsExperience: '', // 期权年资
    warrantBullBearExperience: '', // 认股权证年资
    fxExperience: '', // 外汇年资
    bullionExperience: '', // 贵金属年资
    fundStructuredProductExperience: '', // 基金年资
    virtualAssetExperience: '', // 虚拟资产年资
    hasDerivativeKnowledge: 0, // 0 / 1 是否有衍生产品相关知识或交易
    hasDerivativeTraining: 0, // 0 / 1 是否曾接受培训或修读相关课程
    hasDerivativeWorkExperience: 0, // 0 / 1 是否现时或过去拥有相关工作经验
    hasDerivativeTrade5times: 0, // 0 / 1 过去 3 年是否操作过 5 次以上衍生品交易
    intendTradeDerivative: '', // 是否有意向买卖衍生工具产品：0-否 1-是
  },
  taxInfoForm: {
    isUsPerson: 0, // 税务居民：默认否
    bankAccount: '',
    bankBranchName: '',
    country: '',
    fpsIdentificationCode: '',
    taxNumber: '',
  },
  taxCheck1: false,
  taxCheck2: false,
  othersForm: {
    isSfcLicensed: 0, // 是否 SFC 持牌/注册人士
    sfcLicenseDesc: '',
    isRelativeToStaff: 0, // 是否与公司职员有亲属关系
    staffRelativeDesc: '',
    isSpouseHoldCompanyAccount: 0, // 配偶是否持有本公司账户
    spouseHoldCompanyAccountDesc: '', // 配偶姓名,账户号码
    spouseHoldName: '', // UI：配偶姓名（由 Desc 拆分回填）
    spouseHoldAccountNo: '', // UI：账户号码
    isControlOtherClientVoting35: 0, // 是否控制其他客户 ≥35% 投票权
    controlOtherClientVoting35Desc: '', // 户名,账户号码
    controlVotingAccountName: '', // UI：户名
    controlVotingAccountNo: '', // UI：账户号码
  },
  activateEleServicesForm: {
    list: {},
    selected: '',
  },
  selectAccountForm: {
    securitiesList: [],
    securitiesAccountType: '', // 证券账户（可多选，逗号隔开）
    fundList: [],
    fundAccountType: '', // 基金账户（可多选，逗号隔开）
    tradePermissionList: [],
    needDerivativeTradePermission: false, // 交易权限：是否需要衍生品交易权限
  },
  currency: {
    accountType: '',
  },
  bank: {
    bankCode: '',
    brokerBankCode: '',
    bankName: '',
    bankAccountNo: '',
    bankAccountName: '',
    bankAccountType: '',
    bankCardPurpose: 'TRANSFER',
  },
  /** 转账确认页展示：userBankBindList 第一项的银行名、编码与脱敏账号 */
  userBankView: {
    bankName: '',
    bankCode: '',
    brokerBankCode: '',
    bankAccountNo: '',
  },
  attachmentList: [], // 上传文件

  offline: {
    address: '',
    date: '',
    time: '',
    name: '',
    phone: '',
  },

  auditInfo: null as any,
  errorKeys: [],
  errorValues: [],
  errorMaps: {},

  editPages: [],

  /** 是否处于「被退回资料修改」模式（由 URL mode=editRefused 触发） */
  editRefusedMode: false,
  /**
   * editRefused 流程阶段：
   *   forms  → 表单修正步骤
   *   risk   → 风险披露 + SZCA 人脸（仅大陆，对齐开户 RiskInfo）
   *   video  → 录制活体视频（已停用，保留类型兼容；流程不再进入）
   *   sign   → 电子签名（Signature 组件）
   *   done   → 完成，发送 WEB_TO_APP_FINISHOPEN
   * 大陆：forms → risk → sign → done；非大陆：forms → sign → done。
   */
  editRefusedPhase: 'forms' as 'forms' | 'video' | 'risk' | 'sign' | 'done',
  /** editRefused forms 阶段当前纠错页下标；返回 forms 时恢复 */
  editRefusedFormStep: 0,

  /**
   * 财务回退续办入金（URL mode=editRefused & role=Activity_admin_11|22）。
   * 与运营纠错 editRefusedMode 互斥：关纠错壳、走 OnLine 入金步进，资料由 setState 回填。
   */
  financeTransferResumeMode: false,
  /** 财务回退时保留的 role，供 routeTo 写回 URL */
  financeTransferResumeRole: '',

  /**
   * BACK 且无修改项：从开户第一步重走正常流程，保留已填数据
   *（由 URL mode=backReopen&step=0 触发；与 editRefused 完全独立）
   */
  backReopenMode: false,

  editMode: false, // 是否从「确认资料」页进入单项修改模式
  editReturnStep: 0, // 修改完成 / 取消后要返回的步骤（确认页）
  editStartStep: 0, // 编辑区间起始步骤
  editEndStep: 0, // 编辑区间结束步骤（多页修改时与起始不同）
  editDirty: false, // 区间内是否已有页面提交过（提交即落库，「返回」不再还原）
  /**
   * CA / 指纹校验失败后已自动跳过一次身份证或银行卡页。
   * 避免从编辑页「返回」RiskInfo 时 onMount 再次自动踢走。
   */
  profilePassAutoRedirected: false,
  /**
   * CA 资料双页纠错（身份证 → 银行卡）：
   * 身份证页隐藏返回；银行卡页返回直接回 RiskInfo（不回身份证）。
   */
  profilePassDualFix: false,
});

// ── 真实访问栈（前进 push / 后退 pop；step 不必单调 ±1）──────────────────────

export type VisitEntry = {
  step: number
  /** 仅 editRefused；普通开户省略 */
  editRefusedPhase?: 'forms' | 'video' | 'risk' | 'sign' | 'done'
}

/** 栈顶 = 当前页；与 pState.records / current 同步 */
let visitStack: VisitEntry[] = [{ step: 0 }]

/**
 * 本次会话真正的「入口底线步」：
 * - 全新开户从 0 进 → 0
 * - 续办恢复 → 恢复到的步骤
 * - 深链续入（?step=）→ 该 step
 *
 * 仅用于「App 顶栏返回 / 物理返回」触底判定：current <= 该值即退出到 App，
 * 不再把用户本次没走过的前置步骤一格格倒退。「上一步」按钮不受此值影响，
 * 仍走 lastProcess / popVisit，可回看更前面已填步骤。
 */
let openEntryFloorStep = 0

const syncRecordsFromVisitStack = () => {
  pState.records = visitStack.map((e) => e.step)
  const top = visitStack[visitStack.length - 1]
  pState.current = top?.step ?? 0
}

const peekVisit = (): VisitEntry => visitStack[visitStack.length - 1] ?? { step: 0 }

/** 保留 mode / role / lang / theme 等，URL step 写真实 destination */
const routeToVisit = (entry: VisitEntry) => {
  const q = (location.query || {}) as Record<string, string | undefined>
  const params = new URLSearchParams()
  params.set('step', String(entry.step))

  // 财务回退也带 mode=editRefused（App 约定），但不走 editRefused 纠错壳
  if (pState.editRefusedMode || pState.financeTransferResumeMode || q.mode === 'editRefused') {
    params.set('mode', 'editRefused')
    if (pState.editRefusedMode && entry.editRefusedPhase && entry.editRefusedPhase !== 'forms') {
      params.set('phase', entry.editRefusedPhase)
    }
  } else if (pState.backReopenMode || q.mode === 'backReopen') {
    params.set('mode', 'backReopen')
  }

  const role = pState.financeTransferResumeRole || q.role
  if (role) params.set('role', role)
  if (q.backType) params.set('backType', q.backType)

  for (const key of ['lang', 'theme', 'webVersion', 'webversion']) {
    const v = q[key]
    if (v) params.set(key, v)
  }

  location.routeTo(`/open-progress?${params.toString()}`)
}

const applyVisit = (
  entry: VisitEntry,
  opts?: { notify?: boolean; completePrev?: boolean; prevStep?: number; route?: boolean },
) => {
  const prevStep = opts?.prevStep ?? pState.current
  pState.current = entry.step
  if (entry.editRefusedPhase && pState.editRefusedMode) {
    pState.editRefusedPhase = entry.editRefusedPhase
  }
  syncRecordsFromVisitStack()
  if (opts?.route !== false) {
    routeToVisit(entry)
  }

  if (opts?.notify === false) return
  if (prevStep !== entry.step) {
    notifyKycStepChange(prevStep, entry.step, isMainlandIdCard(), {
      completePrev: opts?.completePrev === true,
    })
  } else {
    syncKycStepView(entry.step, isMainlandIdCard())
  }
}

/** 前进：push 目标页 */
export const pushVisit = (
  entry: VisitEntry,
  opts?: { completePrev?: boolean; route?: boolean },
) => {
  const prevStep = pState.current
  const top = peekVisit()
  // 同一页面重复 push 无意义（避免双击）
  if (top.step === entry.step && top.editRefusedPhase === entry.editRefusedPhase) {
    applyVisit(entry, { notify: false, prevStep, route: opts?.route })
    return
  }
  visitStack.push({
    step: entry.step,
    ...(entry.editRefusedPhase ? { editRefusedPhase: entry.editRefusedPhase } : {}),
  })
  applyVisit(peekVisit(), {
    prevStep,
    completePrev: opts?.completePrev === true,
    route: opts?.route,
  })
}

/**
 * 跳转并保留来路（签名回 Risk 等）。
 * 与 setRecords 不同：不把历史改写成 0..n。
 */
export const jumpVisit = (step: number, opts?: { editRefusedPhase?: VisitEntry['editRefusedPhase']; route?: boolean }) => {
  pushVisit(
    {
      step,
      ...(opts?.editRefusedPhase ? { editRefusedPhase: opts.editRefusedPhase } : {}),
    },
    { completePrev: false, route: opts?.route },
  )
}

/** 后退：pop；栈见底则通知 App 退出 / 纯 H5 回账户 */
export const popVisit = (opts?: { saveCache?: boolean }): boolean => {
  if (visitStack.length <= 1) {
    if ((window as any).ReactNativeWebView) {
      postToApp({ type: 'WEB_TO_APP_GoBack' })
    } else {
      location.routeTo('/account')
    }
    return false
  }
  const prevStep = pState.current
  visitStack.pop()
  applyVisit(peekVisit(), { prevStep, completePrev: false })
  // 单项编辑：若已回到确认页（editReturnStep），清掉编辑态，避免底栏仍显示「返回/保存」
  if (pState.editMode && pState.current === pState.editReturnStep) {
    editSnapshot = null
    pState.editMode = false
    pState.editDirty = false
    pState.profilePassDualFix = false
  }
  if (opts?.saveCache !== false && !pState.editRefusedMode) {
    saveProgressCache(pState.current)
  }
  return true
}

/**
 * 冷启动 / 深链续办兜底：无真实路径时 seed 为 0..n。
 * 业务跳转请用 jumpVisit / pushVisit，勿再靠本函数清栈。
 * @param route 默认 false（与旧 setRecords 一致，由调用方统一 routeTo）
 */
export const replaceVisitStackSeed = (step: number, opts?: { route?: boolean; notify?: boolean }) => {
  const n = Math.max(0, Math.floor(step))
  visitStack = Array.from({ length: n + 1 }, (_, i) => ({ step: i }))
  applyVisit(peekVisit(), {
    notify: opts?.notify !== false,
    completePrev: false,
    route: opts?.route === true,
  })
}

/** 截断到目标步（若栈中有则裁到该帧；否则 seed） */
const truncateVisitStackToStep = (step: number) => {
  const prevStep = pState.current
  let idx = -1
  for (let i = visitStack.length - 1; i >= 0; i--) {
    if (visitStack[i].step === step) {
      idx = i
      break
    }
  }
  if (idx >= 0) {
    visitStack = visitStack.slice(0, idx + 1)
    applyVisit(peekVisit(), { prevStep, completePrev: false })
    return
  }
  replaceVisitStackSeed(step, { route: true })
}

// ── 单项修改（编辑模式）─────────────────────────────────────────────────────
// 表单页双向绑定到 pState，编辑会实时改 pState；「返回」需用快照还原

let editSnapshot: any = null;
/** 最近一次 getByUserId / setState 对齐的证件类型；用于身份变更后按需再拉全量 */
let syncedIdCardType = '';

/**
 * 确认资料页改证件时切换身份：同步编辑区间与返回确认页步号。
 * - 切到大陆：区间需包含银联页（否则缺卡后续会报错）；确认页 13→14
 * - 切出大陆：收窄到证件上传页；确认页 14→13
 */
export const syncEditRangeAfterIdentityChange = (identityType: string) => {
  if (!pState.editMode) return;
  const mainland = identityType === 'MAINLAND_CHINA';
  // 与 OnlineProgress CompMaps 对齐：大陆 ConfirmInfos=14，非大陆=13
  pState.editReturnStep = mainland ? 14 : 13;
  if (mainland) {
    const bankStep = STEPS_MAINLAND.UploadBank;
    if (pState.editEndStep < bankStep) {
      pState.editEndStep = bankStep;
    }
  } else if (pState.editEndStep > STEPS_NON_MAINLAND.UploadLic) {
    // 避免下一步落到非大陆流程不存在的银联步
    pState.editEndStep = STEPS_NON_MAINLAND.UploadLic;
  }
};

/**
 * 进入单项修改。支持单页或区间多页：
 * - `editProcess(8)`        修改单页（如税务信息）
 * - `editProcess(2, 3)`     修改跨页区间（如证件信息：SelectIDCard + UploadLic）
 */
export const editProcess = (startStep: number, endStep?: number) => {
  pState.editReturnStep = pState.current;
  pState.editStartStep = startStep;
  pState.editEndStep = endStep ?? startStep;
  pState.editMode = true;
  pState.editDirty = false;
  editSnapshot = JSON.parse(
    JSON.stringify({
      uploadLicForm: pState.uploadLicForm,
      addressForm: pState.addressForm,
      addressCnChecked: pState.addressCnChecked,
      jobStatusForm: pState.jobStatusForm,
      financialStatusForm: pState.financialStatusForm,
      investmentExpForm: pState.investmentExpForm,
      taxInfoForm: pState.taxInfoForm,
      selectAccountForm: pState.selectAccountForm,
    }),
  );
  // 保留确认页在栈中，返回时可 pop 回去
  pushVisit({ step: startStep }, { completePrev: false });
};

/**
 * 退出编辑模式并返回确认页。
 * @param restore 为 true（「返回」）时尝试用快照还原；但若区间内已有页面提交过
 *                （`editDirty`，数据已落库），则不还原以保持与服务端一致。
 */
export const exitEditProcess = (restore = false) => {
  if (restore && !pState.editDirty && editSnapshot) {
    pState.uploadLicForm = editSnapshot.uploadLicForm;
    pState.addressForm = editSnapshot.addressForm;
    pState.addressCnChecked = !!editSnapshot.addressCnChecked;
    pState.jobStatusForm = editSnapshot.jobStatusForm;
    pState.financialStatusForm = editSnapshot.financialStatusForm;
    pState.investmentExpForm = editSnapshot.investmentExpForm;
    pState.taxInfoForm = editSnapshot.taxInfoForm;
    pState.selectAccountForm = editSnapshot.selectAccountForm;
  }
  editSnapshot = null;
  pState.editMode = false;
  pState.editDirty = false;
  pState.profilePassDualFix = false;
  truncateVisitStackToStep(pState.editReturnStep || 0);
};

/**
 * 编辑模式下的「返回」：
 * - profilePassDualFix 且已在银行卡页 → 直接回 RiskInfo（不回身份证）；
 * - 区间中间页 → 退回上一页（保留已填）；
 * - 区间首页 → 取消整个修改，还原快照（未提交过时）回确认页。
 */
export const editLastProcess = () => {
  if (pState.profilePassDualFix && pState.current > pState.editStartStep) {
    exitEditProcess(true);
    return;
  }
  if (pState.current > pState.editStartStep) {
    popVisit({ saveCache: false });
    return;
  }
  exitEditProcess(true);
};

export const nextProcess = async (step?: number) => {
  // 被退回修改流程：阶段推进也进访问栈，便于 App 顶栏原路返回
  if (pState.editRefusedMode) {
    if (pState.editRefusedPhase === 'risk') {
      pushVisit({ step: pState.current, editRefusedPhase: 'sign' }, { completePrev: false });
    } else if (pState.editRefusedPhase === 'sign') {
      pushVisit({ step: pState.current, editRefusedPhase: 'done' }, { completePrev: false });
    } else if (pState.editRefusedPhase === 'video') {
      // 旧状态兼容：大陆进 risk，非大陆进 sign
      const phase = isMainlandIdCard() ? 'risk' : 'sign';
      pushVisit({ step: pState.current, editRefusedPhase: phase }, { completePrev: false });
    }
    return;
  }

  // 编辑模式：区间未走完则前进；确认资料单项修改的末页只标记已保存，留在当前页。
  // CA 双页纠错末页仍退出并回风险披露。
  if (pState.editMode) {
    pState.editDirty = true; // 当前页已提交（落库），「返回」不再还原快照
    if (pState.current < pState.editEndStep) {
      pushVisit({ step: pState.current + 1 }, { completePrev: false });
      return;
    }
    if (pState.profilePassDualFix) {
      exitEditProcess(false);
      return;
    }
    showToast(i18n.OpenProgress.Common.ToastSaveSuccess);
    return;
  }

  const dest = typeof step === 'number' && Number.isFinite(step) ? step : pState.current + 1;
  pushVisit({ step: dest }, { completePrev: true });
  saveProgressCache(dest);
};

/** 网上转账提交成功页（Reviewing）：大陆 21 / 非大陆 20 */
const isReviewingStep = () =>
  isMainlandIdCard() ? pState.current === 21 : pState.current === 20;

/** 提交成功态：流程内 Reviewing，或门禁结果页 account-result */
const isOnSubmittedSuccessView = () => {
  const path = String(location.path || '');
  if (path === '/account-result' || path.startsWith('/account-result?')) {
    const type = String(location.query?.type || pState.status || '');
    return type === 'SUBMITTED' || type === 'APPROVING';
  }
  // 流程内 Reviewing（含 BACK）：终端页，返回即退出，勿退回银行卡确认
  return isReviewingStep();
};

/** 提交成功页返回：与页底「完成」一致，退出开户 H5 */
const exitAfterSubmitSuccess = () => {
  void import('./showSubmitSuccessModal').then(({ finishOpenAccount }) => {
    finishOpenAccount();
  });
};

/** 同一 tick 内只 pop 一次（App 曾把 GoBack 双发到 receiveFromRN + __rnInternalReceive） */
let lastProcessBusy = false;

export const lastProcess = (_step?: number) => {
  if (lastProcessBusy) return;
  // 确认资料单项修改：与底栏「返回」一致（清 editMode），勿只 popVisit
  if (
    pState.editMode &&
    !(typeof _step === 'number' && Number.isFinite(_step))
  ) {
    lastProcessBusy = true;
    try {
      editLastProcess();
    } finally {
      queueMicrotask(() => {
        lastProcessBusy = false;
      });
    }
    return;
  }
  lastProcessBusy = true;
  try {
    // 提交成功页（Reviewing / account-result）：返回即退出，勿退回银行卡确认
    if (isOnSubmittedSuccessView()) {
      exitAfterSubmitSuccess();
      return;
    }

    // 被退回修改：优先 pop 访问栈（含 phase）；栈见底再退出
    if (pState.editRefusedMode) {
      if (visitStack.length > 1) {
        popVisit({ saveCache: false });
        return;
      }
      if ((window as any).ReactNativeWebView) {
        postToApp({ type: 'WEB_TO_APP_GoBack' });
      } else {
        location.routeTo('/account');
      }
      return;
    }

    // 显式目标步（极少用）：截断到该步；否则 pop
    if (typeof _step === 'number' && Number.isFinite(_step)) {
      truncateVisitStackToStep(Math.max(0, _step));
      saveProgressCache(pState.current);
      return;
    }

    popVisit({ saveCache: true });
  } finally {
    queueMicrotask(() => {
      lastProcessBusy = false;
    });
  }
};

/** 开户表单空初始值：仅保留结构性默认（证件类型 / 区号 / 用途常量等）；性别默认 null 需用户选择 */
const emptyUploadLicForm = () => ({
  firstNameEn: '',
  lastNameEn: '',
  fullName: '',
  pinyinName: '',
  gender: null,
  identityType: '',
  idCardType: 'MAINLAND_CHINA',
  idCardNo: '',
  idCardStartDate: '',
  idCardEndDate: '',
  idCardLongTerm: 0,
  birthCountry: '',
  nationality: '',
  birthDate: '',
  idCardIssueAuthority: '',
});

const emptyUnionPayCard = () => ({
  bankAccountNo: '',
  bankName: '',
  bankCardPurpose: 'UNION_PAY_AUTH',
  phoneCode: '+86',
  phone: '',
});

type RegisteredUserMsg = {
  userMsg?: { phone?: string; countryCode?: string; email?: string };
};

/** 注册手机号：首次开户取 userInfo.userMsg.phone */
const registeredUserPhone = () => {
  const info = userState.userInfo as RegisteredUserMsg | null;
  return String(info?.userMsg?.phone || userState.phone || '').trim();
};

/** 注册区号：首次开户取 userInfo.userMsg.countryCode */
const registeredUserCountryCode = () => {
  const info = userState.userInfo as RegisteredUserMsg | null;
  return String(info?.userMsg?.countryCode || userState.countryCode || '').trim();
};

/** 注册邮箱：首次开户取 userInfo.userMsg.email */
const registeredUserEmail = () => {
  const info = userState.userInfo as RegisteredUserMsg | null;
  return String(info?.userMsg?.email || userState.email || '').trim();
};

/** 注册邮箱有值则锁定不可改；无则允许编辑 */
export const isAddressEmailLocked = () => !!registeredUserEmail();

/** 地址联系电话：已有保存值不覆盖；空值（含首次注册）用注册手机号带入 */
const resolveAddressPhone = (savedPhone: unknown) => {
  const saved = String(savedPhone ?? '').trim();
  return saved || registeredUserPhone();
};

/** 地址联系电话区号：已有保存值不覆盖；空值（含首次注册）用注册区号带入 */
const resolveAddressPhoneCountry = (savedPhoneCountry: unknown) => {
  const saved = String(savedPhoneCountry ?? '').trim();
  return saved || registeredUserCountryCode();
};

/** 地址邮箱：已有保存值不覆盖；空值用注册邮箱带入 */
const resolveAddressEmail = (savedEmail: unknown) => {
  const saved = String(savedEmail ?? '').trim();
  return saved || registeredUserEmail();
};

export const fillAddressPhoneIfEmpty = () => {
  if (!String(pState.addressForm.phone || '').trim()) {
    const phone = registeredUserPhone();
    if (phone) pState.addressForm.phone = phone;
  }
  if (!String(pState.addressForm.phoneCountry || '').trim()) {
    const phoneCountry = registeredUserCountryCode();
    if (phoneCountry) pState.addressForm.phoneCountry = phoneCountry;
  }
};

/** 通讯地址类型单选。历史逗号多值按选项顺序只保留第一项。 */
const ADDRESS_TYPE_ORDER = ['RESIDENTIAL', 'BUSINESS', 'OTHER'] as const;

export const normalizeAddressType = (value: unknown) => {
  const codes = String(value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return ADDRESS_TYPE_ORDER.find((item) => codes.includes(item)) || '';
};

/** 地址邮箱空值时用注册邮箱带入（有注册邮箱则字段只读） */
export const fillAddressEmailIfEmpty = () => {
  if (!String(pState.addressForm.email || '').trim()) {
    const email = registeredUserEmail();
    if (email) pState.addressForm.email = email;
  }
};

const emptyAddressForm = () => ({
  country: '',
  address: '',
  addressCn: '',
  correspondenceAddress: '',
  addressType: '',
  addressTypeOther: '',
  phoneCountry: '',
  phone: '',
  email: '',
});

const emptyJobStatusForm = () => ({
  education: '',
  employmentStatus: '',
  companyName: '',
  industry: '',
  industryOther: '',
  position: '',
  positionOther: '',
  companyAddress: '',
  employmentYears: '',
  companyPhone: '',
});

const emptyFinancialStatusForm = () => ({
  housingProperty: '',
  annualIncome: '',
  totalAssetsNetHkd: '',
  wealthSource: '',
  wealthSourceOther: '',
  sourceOfFunds: '',
  sourceOfFundsOther: '',
  fundTransferMethod: '',
  sourceOfFundsLocation: '',
  sourceOfFundsLocationOther: '',
});

const emptyInvestmentExpForm = () => ({
  investmentKnowledge: '',
  investmentExperienceTypes: '',
  investmentTarget: '',
  investmentPurposeOther: '',
  stockExperience: '',
  futuresExperience: '',
  optionsExperience: '',
  warrantBullBearExperience: '',
  fxExperience: '',
  bullionExperience: '',
  fundStructuredProductExperience: '',
  virtualAssetExperience: '',
  hasDerivativeKnowledge: 0,
  hasDerivativeTraining: 0,
  hasDerivativeWorkExperience: 0,
  hasDerivativeTrade5times: 0,
  intendTradeDerivative: '',
});

/** 0/1 勾选字段：兼容 number / string；其它或空 → 0 */
const toFlag01 = (raw: unknown): 0 | 1 => (raw === 1 || raw === '1' ? 1 : 0);

/**
 * 税务居民：空或无法识别 → 默认否(0)。
 * 历史字段是 0/1（及 '0'/'1'）；W-8BEN 写入的 isAmericanCitizen 是 boolean，否会是 false。
 */
const resolveIsUsPerson = (raw: unknown): 0 | 1 => {
  if (raw == null || raw === '') return 0;
  if (raw === true || raw === 1 || raw === '1') return 1;
  if (raw === false || raw === 0 || raw === '0') return 0;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (s === 'true') return 1;
    if (s === 'false') return 0;
  }
  return 0;
};

const emptyTaxInfoForm = () => ({
  isUsPerson: 0,
  bankAccount: '',
  bankBranchName: '',
  country: '',
  fpsIdentificationCode: '',
  taxNumber: '',
});

const emptyOthersForm = () => ({
  isSfcLicensed: 0,
  sfcLicenseDesc: '',
  isRelativeToStaff: 0,
  staffRelativeDesc: '',
  isSpouseHoldCompanyAccount: 0,
  spouseHoldCompanyAccountDesc: '',
  spouseHoldName: '',
  spouseHoldAccountNo: '',
  isControlOtherClientVoting35: 0,
  controlOtherClientVoting35Desc: '',
  controlVotingAccountName: '',
  controlVotingAccountNo: '',
});

const emptySelectAccountForm = () => ({
  securitiesList: [],
  securitiesAccountType: '',
  fundList: [],
  fundAccountType: '',
  tradePermissionList: [],
  needDerivativeTradePermission: false,
});

/** 非字典银行：券商编码传 OTHER，交易行号提交时留空，名称走 bankName */
export const OTHER_BROKER_BANK_CODE = 'OTHER';

const emptyBank = () => ({
  bankCode: '',
  brokerBankCode: '',
  bankName: '',
  bankAccountNo: '',
  bankAccountName: '',
  bankAccountType: '',
  bankCardPurpose: 'TRANSFER',
});

const emptyUserBankView = () => ({
  bankName: '',
  bankCode: '',
  brokerBankCode: '',
  bankAccountNo: '',
});

/** 确认页只展示用户自己的银行卡：取第一项；空或非数组则清空 */
export const applyUserBankView = (list: unknown) => {
  const row = Array.isArray(list) && list.length ? list[0] : null;
  pState.userBankView.bankName = String(row?.bankName ?? '').trim();
  pState.userBankView.bankCode = String(row?.bankCode ?? '').trim();
  pState.userBankView.brokerBankCode = String(row?.brokerBankCode ?? '').trim();
  pState.userBankView.bankAccountNo = String(row?.bankAccountNo ?? '').trim();
};

const emptyOffline = () => ({
  address: '',
  date: '',
  time: '',
  name: '',
  phone: '',
});

/**
 * 拒绝重开 / 进页重置：清空全部用户填写与 CA / 附件 / 纠错态，只留结构性默认值。
 * 不写入 userBase.phone 等业务回填。
 */
export const wipeOpenProgressMemory = () => {
  clearProgressCache();
  clearSzcaPendingState();
  clearSzcaCertCache();
  editSnapshot = null;
  syncedIdCardType = '';
  setFormApi(null);
  setFormValidateFunc(null);

  pState.fourOrderno = '';
  pState.certP10 = '';
  pState.certDn = '';
  pState.keyPair = null;
  pState.certSn = '';
  pState.id = '';
  pState.status = '';
  pState.progress = 0;
  pState.disable = false;
  pState.taxName = '';
  pState.companyAddress = '';

  pState.uploadLicForm = emptyUploadLicForm();
  pState.unionPayCard = emptyUnionPayCard();
  pState.addressForm = emptyAddressForm();
  pState.addressCnChecked = false;
  pState.jobStatusForm = emptyJobStatusForm();
  pState.financialStatusForm = emptyFinancialStatusForm();
  pState.investmentExpForm = emptyInvestmentExpForm();
  pState.taxInfoForm = emptyTaxInfoForm();
  pState.taxCheck1 = false;
  pState.taxCheck2 = false;
  pState.othersForm = emptyOthersForm();
  pState.activateEleServicesForm = { list: {}, selected: '' };
  pState.selectAccountForm = emptySelectAccountForm();
  pState.currency = { accountType: '' };
  pState.bank = emptyBank();
  pState.userBankView = emptyUserBankView();
  pState.attachmentList = [];
  pState.offline = emptyOffline();

  pState.auditInfo = null;
  pState.errorKeys = [];
  pState.errorValues = [];
  pState.errorMaps = {};
  pState.editPages = [];

  pState.editRefusedMode = false;
  pState.editRefusedPhase = 'forms';
  pState.editRefusedFormStep = 0;
  pState.financeTransferResumeMode = false;
  pState.financeTransferResumeRole = '';
  pState.backReopenMode = false;
  pState.editMode = false;
  pState.editReturnStep = 0;
  pState.editStartStep = 0;
  pState.editEndStep = 0;
  pState.editDirty = false;
  pState.profilePassAutoRedirected = false;
  pState.profilePassDualFix = false;
};

/** 拒绝开新单后：只挂新单壳 + 券商展示信息，不回填用户 KYC（含 base.phone） */
const applyNewApplyShell = (data: any, newApply?: any) => {
  const company = data?.securitiesCompanyInfo || {};
  pState.id = data?.id || newApply?.id || '';
  pState.status = data?.status || '';
  pState.applyMethod = data?.applyMethod || 'ONLINE_TRANSFER';
  pState.progress = 0;
  pState.attachmentList = [];
  // 券商静态展示（非用户填写）
  pState.taxName = company.taxName || '';
  pState.companyAddress = company.companyAddress || '';
  pState.taxInfoForm = {
    ...emptyTaxInfoForm(),
    bankAccount: company.bankAccount || '',
    bankBranchName: company.bankBranchName || '',
    country: company.country || '',
    fpsIdentificationCode: company.fpsIdentificationCode || '',
    taxNumber: company.taxNumber || '',
  };
};

export const resetpState = () => {
  wipeOpenProgressMemory();
  pState.loading = true;
  pState.shellReady = false;
  pState.applyReady = false;
  pState.loadError = false;
  pState.loadErrorMessage = '';
  visitStack = [{ step: 0 }];
  openEntryFloorStep = 0;
  syncRecordsFromVisitStack();
  pState.applyMethod = 'ONLINE_TRANSFER';
  didPrimeShell = false;
};

/**
 * 冷启动 / 深链续办：无真实访问路径时 seed 为 0..n。
 * 业务内跳转请用 jumpVisit / pushVisit。
 */
export const setRecords = (current) => {
  const seedStep = typeof current === 'number' ? current : 0;
  // 入口 seed（续办 / 深链 / 全新 / 终态 / backReopen）即本次会话入口底线步
  openEntryFloorStep = Math.max(0, Math.floor(seedStep));
  replaceVisitStackSeed(seedStep, { route: false });
};

/** encryptKey 缺失时跳过解密；调用方应先 waitForEncryptKey */
const safeDecrypt = (encryptedHex?: string) => {
  console.log('[encryptKey][decrypt]', {
    hasKey: !!commonState.encryptKey,
    key: commonState.encryptKey || '(empty)',
    cipherLen: encryptedHex?.length ?? 0,
  });
  if (!encryptedHex || !commonState.encryptKey) return '';
  try {
    return aesDecryptHexByPass(encryptedHex, commonState.encryptKey);
  } catch {
    console.log('[encryptKey][decrypt][fail]');
    return '';
  }
};

/** 超时后 key 仍晚到：补解密证件号 / 银联卡号 / 转账银行卡号（仅当表单对应字段仍为空） */
const scheduleDecryptRetry = (opts: {
  idCardNoEncrypt?: string;
  unionBankAccountNoEncrypt?: string;
  transferBankAccountNoEncrypt?: string;
}) => {
  if (commonState.encryptKey) return;
  const { idCardNoEncrypt, unionBankAccountNoEncrypt, transferBankAccountNoEncrypt } = opts;
  if (!idCardNoEncrypt && !unionBankAccountNoEncrypt && !transferBankAccountNoEncrypt) return;
  void waitForEncryptKey().then((key) => {
    if (!key) return;
    if (idCardNoEncrypt && !pState.uploadLicForm.idCardNo) {
      pState.uploadLicForm.idCardNo = safeDecrypt(idCardNoEncrypt);
    }
    if (unionBankAccountNoEncrypt && !pState.unionPayCard.bankAccountNo) {
      pState.unionPayCard.bankAccountNo = safeDecrypt(unionBankAccountNoEncrypt);
    }
    if (transferBankAccountNoEncrypt && !pState.bank.bankAccountNo) {
      pState.bank.bankAccountNo = safeDecrypt(transferBankAccountNoEncrypt);
    }
  });
};

const setState = (data) => {
  const base = data.userBase || {};
  const userCareer = data.userCareer;
  const userAccount = data.userAccountInfo;
  const company = data.securitiesCompanyInfo;
  const bindList = Array.isArray(data.userBankBindList) ? data.userBankBindList : [];
  applyUserBankView(bindList);
  const bank = bindList.find((i) => i.bankCardPurpose === 'TRANSFER');
  const uniBank = bindList.find((i) => i.bankCardPurpose === 'UNION_PAY_AUTH');
  // pState.uploadLicForm.fullName = baseInfo.fullName;
  pState.taxName = company.taxName;
  const idCardType = base.idCardType || 'MAINLAND_CHINA';
  syncedIdCardType = idCardType;
  const idCardNo = base.idCardNoEncrypt ? safeDecrypt(base.idCardNoEncrypt) : '';
  const unionBankAccountNo = uniBank?.bankAccountNoEncrypt
    ? safeDecrypt(uniBank.bankAccountNoEncrypt)
    : '';
  // 转账银行卡：优先解密 encrypt 字段（明文 bankAccountNo 多为脱敏）
  const transferBankAccountNo = bank?.bankAccountNoEncrypt
    ? safeDecrypt(bank.bankAccountNoEncrypt)
    : bank?.bankAccountNo || '';
  const names = readNameParts(base);
  pState.uploadLicForm = {
    firstNameEn: names.firstNameEn,
    lastNameEn: names.lastNameEn,
    fullName: names.fullName,
    pinyinName: names.pinyinName,
    gender: base.gender || null, // 性别（无值保持 null，展示「请选择」）
    identityType: base.identityType || base.idCardType || '', // 身份类型
    idCardType, // 证件类型
    idCardNo, // 证件号码（来自 idCardNoEncrypt 解密）
    idCardStartDate: base.idCardStartDate || '', // 证件有效期 开始时间
    idCardEndDate: base.idCardEndDate || '', // 证件有效期 结束时间
    idCardLongTerm: base.idCardLongTerm ?? 0, // 是否长期有效 1:是  0:否
    birthCountry: base.birthCountry || '', // 出生地
    nationality: base.nationality || '', // 国籍
    birthDate: base.birthDate || '',
    idCardIssueAuthority: base.idCardIssueAuthority || '',
  };

  // 无银联卡时也必须重置，避免内存残留旧卡号/手机号
  pState.unionPayCard = uniBank
    ? {
        bankAccountNo: unionBankAccountNo,
        bankName: uniBank.bankName || '',
        bankCardPurpose: 'UNION_PAY_AUTH',
        phoneCode: uniBank.phoneCode || '+86',
        phone: uniBank.phone || '',
      }
    : emptyUnionPayCard();

  // key 尚未就绪时登记补解密（wait 超时后 Theme/config 晚到）
  if (
    (base.idCardNoEncrypt && !idCardNo) ||
    (uniBank?.bankAccountNoEncrypt && !unionBankAccountNo) ||
    (bank?.bankAccountNoEncrypt && !transferBankAccountNo)
  ) {
    scheduleDecryptRetry({
      idCardNoEncrypt: base.idCardNoEncrypt,
      unionBankAccountNoEncrypt: uniBank?.bankAccountNoEncrypt,
      transferBankAccountNoEncrypt: bank?.bankAccountNoEncrypt,
    });
  }

  // 已填过地址则视为 KYC 已存，联系电话/区号/邮箱以保存值为准；空值再用注册信息带入
  const hasAddressFilled = !!(base.address || base.correspondenceAddress || base.country || base.email);
  const addressType = normalizeAddressType(base.addressType);
  const mailingDetail =
    addressType === 'OTHER'
      ? String(base.correspondenceAddress || base.addressTypeOther || '').trim()
      : '';
  pState.addressForm = {
    country: base.country || '', // 居住国家
    address: base.address || '', // 居住地址(英)
    addressCn: base.addressCn || '', // 居住地址(中)
    correspondenceAddress: mailingDetail, // 仅 OTHER 时有详细
    addressType,
    addressTypeOther: mailingDetail,
    phoneCountry: resolveAddressPhoneCountry(hasAddressFilled ? base.phoneCountry || '' : ''),
    phone: resolveAddressPhone(hasAddressFilled ? base.phone || '' : ''),
    email: resolveAddressEmail(hasAddressFilled ? base.email || '' : ''), // 邮箱
  };
  // 回显有中文地址则勾选；勾选本身不落库
  pState.addressCnChecked = !!String(base.addressCn || '').trim();

  pState.jobStatusForm = {
    education: userCareer?.education || '', // 教育背景
    employmentStatus: userCareer?.employmentStatus || '', // 受雇状态
    companyName: userCareer?.companyName || '', // 公司名称
    industry: userCareer?.industry || '', // 行业
    industryOther: userCareer?.industryOther || '', // 行业选「其他」时的说明
    position: userCareer?.position || '', // 职位
    positionOther: userCareer?.positionOther || '', // 职位选「其他」时的说明
    companyAddress: userCareer?.companyAddress || '', // 公司地址
    employmentYears: userCareer?.employmentYears || '', // 受雇年期
    companyPhone: userCareer?.companyPhone || '', // 公司电话
  };
  sanitizeEmploymentDetailFields(pState.jobStatusForm);

  pState.financialStatusForm = {
    housingProperty: userCareer?.housingProperty || '',
    annualIncome: userCareer?.annualIncome || '',
    totalAssetsNetHkd: userCareer?.totalAssetsNetHkd || '',
    wealthSource: userCareer?.wealthSource || '',
    wealthSourceOther: userCareer?.wealthSourceOther || '',
    sourceOfFunds: userCareer?.sourceOfFunds || '',
    sourceOfFundsOther: userCareer?.sourceOfFundsOther || '',
    fundTransferMethod: userCareer?.fundTransferMethod || '',
    sourceOfFundsLocation: userCareer?.sourceOfFundsLocation || '',
    sourceOfFundsLocationOther: userCareer?.sourceOfFundsLocationOther || '',
  };

  pState.investmentExpForm = {
    investmentKnowledge: userCareer?.investmentKnowledge || '',
    investmentExperienceTypes: userCareer?.investmentExperienceTypes || '',
    investmentTarget: userCareer?.investmentTarget || '', // 投资目标（可多选，逗号隔开）
    investmentPurposeOther: userCareer?.investmentPurposeOther || '',
    stockExperience: userCareer?.stockExperience || '', // 证券年资
    futuresExperience: userCareer?.futuresExperience || '', // 期货年资
    optionsExperience: userCareer?.optionsExperience || '', // 期权年资
    warrantBullBearExperience: userCareer?.warrantBullBearExperience || '', // 认股权证年资
    fxExperience: userCareer?.fxExperience || '', // 外汇年资
    bullionExperience: userCareer?.bullionExperience || '', // 贵金属年资
    fundStructuredProductExperience: userCareer?.fundStructuredProductExperience || '', // 基金年资
    virtualAssetExperience: userCareer?.virtualAssetExperience || '', // 虚拟资产年资
    hasDerivativeKnowledge: toFlag01(userCareer?.hasDerivativeKnowledge),
    hasDerivativeTraining: toFlag01(userCareer?.hasDerivativeTraining),
    hasDerivativeWorkExperience: toFlag01(userCareer?.hasDerivativeWorkExperience),
    hasDerivativeTrade5times: toFlag01(userCareer?.hasDerivativeTrade5times),
    // 接口返回 number，SelectBox options 为 '0'/'1'，统一转字符串避免展示/勾选对不上
    intendTradeDerivative:
      userCareer?.intendTradeDerivative != null && userCareer?.intendTradeDerivative !== ''
        ? String(userCareer.intendTradeDerivative)
        : '',
  };

  pState.taxInfoForm = {
    isUsPerson: resolveIsUsPerson(userCareer?.isUsPerson),
    bankAccount: company.bankAccount || '',
    bankBranchName: company.bankBranchName || '',
    country: company.country || '',
    fpsIdentificationCode: company.fpsIdentificationCode || '',
    taxNumber: company.taxNumber || '',
  };

  // 其他资料披露：第 3/4 题 Desc 为「字段1,字段2」，回填时拆到 UI 行
  const spouseDesc = userCareer?.spouseHoldCompanyAccountDesc || '';
  const [spouseHoldName = '', spouseHoldAccountNo = ''] = (() => {
    const i = spouseDesc.indexOf(',');
    if (i < 0) return [spouseDesc, ''];
    return [spouseDesc.slice(0, i), spouseDesc.slice(i + 1)];
  })();
  const votingDesc = userCareer?.controlOtherClientVoting35Desc || '';
  const [controlVotingAccountName = '', controlVotingAccountNo = ''] = (() => {
    const i = votingDesc.indexOf(',');
    if (i < 0) return [votingDesc, ''];
    return [votingDesc.slice(0, i), votingDesc.slice(i + 1)];
  })();

  pState.othersForm = {
    isSfcLicensed: userCareer?.isSfcLicensed ?? 0,
    sfcLicenseDesc: userCareer?.sfcLicenseDesc || '',
    isRelativeToStaff: userCareer?.isRelativeToStaff ?? 0,
    staffRelativeDesc: userCareer?.staffRelativeDesc || '',
    isSpouseHoldCompanyAccount: userCareer?.isSpouseHoldCompanyAccount ?? 0,
    spouseHoldCompanyAccountDesc: spouseDesc,
    spouseHoldName,
    spouseHoldAccountNo,
    isControlOtherClientVoting35: userCareer?.isControlOtherClientVoting35 ?? 0,
    controlOtherClientVoting35Desc: votingDesc,
    controlVotingAccountName,
    controlVotingAccountNo,
  };

  const brokerBankCode = String(bank?.brokerBankCode || '');
  const isOtherBank = brokerBankCode === OTHER_BROKER_BANK_CODE;
  pState.bank = {
    bankCode: isOtherBank ? OTHER_BROKER_BANK_CODE : bank?.bankCode || '',
    brokerBankCode,
    bankName: bank?.bankName || '',
    bankAccountNo: transferBankAccountNo,
    bankAccountName: bank?.bankAccountName || '',
    bankAccountType: bank?.bankAccountType || '',
    bankCardPurpose: bank?.bankCardPurpose || 'TRANSFER',
  };

  // 存入币种与银行币种类型同枚举（HKD/USD）；优先申请单/账户，其次银行卡
  pState.currency.accountType =
    data?.accountType || userAccount?.accountType || bank?.bankAccountType || '';

  pState.activateEleServicesForm.selected = data.electronicServices || '';

  pState.selectAccountForm.securitiesAccountType =
    userAccount?.securitiesAccountType || data?.securitiesAccountType || '';
  pState.selectAccountForm.fundAccountType =
    userAccount?.fundAccountType || data?.fundAccountType || '';
  // 交易权限：账户信息或申请单根字段均可回填（0/1 或 boolean）
  const rawNeedPerm =
    userAccount?.needDerivativeTradePermission ?? data?.needDerivativeTradePermission;
  pState.selectAccountForm.needDerivativeTradePermission =
    rawNeedPerm === true || rawNeedPerm === 1 || rawNeedPerm === '1';

  pState.id = data.id;
  pState.status = data.status;

  pState.auditInfo = data.auditInfo || {};
  if (pState.auditInfo.status === 'BACK') {
    const options = parseOpinionPayload(pState?.auditInfo?.opinion);
    const { errorKeys, errorValues, errorMaps } = applyOpinionErrors(options);
    pState.errorKeys = errorKeys;
    pState.errorValues = errorValues;
    pState.errorMaps = errorMaps;
  }

  pState.progress = data.progress;
  pState.attachmentList = data.attachmentList;
  pState.companyAddress = company.companyAddress;
  pState.offline.address = data.appointmentAddress || pState.companyAddress;
  if (data.appointmentTime) {
    const times = data.appointmentTime.split(' ');
    pState.offline.date = times[0];
    pState.offline.time = times[1];
  } else {
    pState.offline.date = dayts.format(Date.now() + 24 * 60 * 60 * 1000, 'YYYY-MM-DD');
    pState.offline.time = '9:00~10:00';
  }
  pState.offline.name = base.fullName || '';
  pState.offline.phone = hasAddressFilled ? base.phone || '' : '';

  console.log('[OpenProgress][setState] applyMethod from API', {
    applyMethod: data.applyMethod,
    applyMethodType: typeof data.applyMethod,
    id: data.id,
    status: data.status,
    progress: data.progress,
  });
  pState.applyMethod = data.applyMethod;

  if (idCardType === 'MAINLAND_CHINA') {
    persistMainlandOpenAccountToken();
  }
};

/**
 * 轻量对齐：仅 getByUserId + setState，无 REFUSED 重开 / 结果页拦截等副作用。
 * 身份类型变更后用于同步附件与后续字段。
 */
export const refreshApplyByUserId = async (applyType = 'NEW_APPLY') => {
  const data = await fetchAllInfos(applyType);
  if (data) {
    await waitForEncryptKey();
    setState(data);
  }
  return data;
};

/** 所选身份与上次对齐值不同时再拉全量；返回是否已刷新 */
export const refreshApplyIfIdentityChanged = async (nextIdentityType: string) => {
  if (!nextIdentityType || nextIdentityType === syncedIdCardType) {
    return false;
  }
  await refreshApplyByUserId('NEW_APPLY');
  return true;
};

const formatLoadErrorMessage = (error: unknown) => {
  const raw = typeof error === 'string' ? error : (error as Error)?.message || '';
  if (String(raw).startsWith('401')) {
    return i18n.OpenProgress.Errors.AuthExpired;
  }
  return raw || i18n.OpenProgress.Errors.LoadFailed;
};

/** 已提交 / 已退回：先 setState，再跳结果页，不可继续走开户（App） */
const BLOCK_OPEN_TO_RESULT = new Set(['SUBMITTED', 'BACK']);
/** H5：提交中 / 审批中 → 结果页「提交中」 */
const H5_PENDING_TO_RESULT = new Set(['SUBMITTED', 'APPROVING']);

export const getAllInfos = async () => {
  try {
    // 首屏：短等 Bootstrap apply seed；NEW_APPLY 匹配则跳过 getByUserId
    // CA 整页回跳（ca=1）：内存已空，必须打 getByUserId，禁止用 Theme seed（可能过期/空）
    await waitForApplySeed();
    const isCaReturn = isSzcaReturnFromQuery();
    const seed = consumeApplySeed('open-progress');
    const APPLY_TYPE = userState.applyStatus !== 'FINISHED' ? 'NEW_APPLY' : 'ADD_ACCOUNT';

    let data: any;
    if (isCaReturn) {
      data = await fetchAllInfos(APPLY_TYPE);
    } else if (seed.used) {
      // applyInfo === null：尚无申请，与接口空结果一致，勿再请求确认
      data = seed.applyInfo;
    } else {
      data = await fetchAllInfos(APPLY_TYPE);
    }

    // 审批拒绝：清空全部 KYC 内存后开新单；只挂新单壳，不 setState 回填（含 base.phone）
    // REFUSED 后必须自行请求最新单，不吃 seed
    let refusedReopened = false;
    if (data?.status === 'REFUSED') {
      wipeOpenProgressMemory();
      pState.loading = true;
      pState.shellReady = false;
      visitStack = [{ step: 0 }];
      syncRecordsFromVisitStack();
      pState.applyMethod = 'ONLINE_TRANSFER';

      const newApply = await saveApply({
        applyType: 'NEW_APPLY',
        ...getApplySaveExtra(),
      });
      console.log(`新开单申请：newApply`, JSON.stringify(newApply));

      data = await fetchAllInfos('NEW_APPLY');
      applyNewApplyShell(data, newApply);
      refusedReopened = true;
    }

    if (data && data.id && !refusedReopened) {
      await waitForEncryptKey();
      setState(data);
    }
    fillAddressPhoneIfEmpty();
    fillAddressEmailIfEmpty();

    pState.loadError = false;
    pState.loadErrorMessage = '';

    // ── 新流程 backReopen：BACK 无 option，从第一步续办；不进结果页、不清理表单 ──
    // 与下方 editRefused / shouldBlockToResult 互不混合
    if (pState.backReopenMode) {
      return { ok: true as const, blocked: false as const };
    }

    // H5：SUBMITTED/APPROVING → 提交中；BACK → 结果页再去修改。editRefused / 财务入金续办不拦截
    // TODO(debug): 本地调试投资经验页，临时关闭「已申请强制跳结果页」；调完务必恢复
    // const applyStatus = pState.status || data?.status || '';
    // const shouldBlockToResult = !pState.editRefusedMode && !pState.financeTransferResumeMode && (
    //   isH5User()
    //     ? H5_PENDING_TO_RESULT.has(applyStatus) || applyStatus === 'BACK'
    //     : BLOCK_OPEN_TO_RESULT.has(applyStatus)
    // );
    // if (shouldBlockToResult) {
    //   clearProgressCache();
    //   // 跳结果页前收起壳，避免深链 step 骨架闪一下
    //   pState.shellReady = false;
    //   location.routeTo(`/account-result?scene=open-apply&type=${applyStatus}`);
    //   return { ok: true as const, blocked: true as const };
    // }

    return { ok: true as const, blocked: false as const };
  } catch (error) {
    return { ok: false as const, message: formatLoadErrorMessage(error) };
  }
};

/** 接口成功后：步骤校准完毕再放开业务分支渲染 */
const markApplyReady = () => {
  pState.shellReady = true;
  pState.loading = false;
  pState.applyReady = true;
};

/** 接口成功后恢复步骤并结束 loading（须在 loading=false / applyReady 前完成步骤校准） */
const finishInitAfterFetch = () => {
  // ── 新流程 backReopen：强制第一步；不清表单数据（setState 已回填）──
  if (pState.backReopenMode) {
    setRecords(0);
    markApplyReady();
    if (!location.query.step) {
      location.routeTo('/open-progress?mode=backReopen&step=0');
    }
    return;
  }

  // ── 财务回退：资料已回填，落到入金首页（或保留深链 step，但不低于 TransferIntro）──
  if (pState.financeTransferResumeMode) {
    const transferIntro = getTransferIntroStep(isMainlandIdCard());
    const stepRaw = location.query.step;
    const urlStep = stepRaw != null && stepRaw !== '' ? +stepRaw : NaN;
    const dest =
      !Number.isNaN(urlStep) && urlStep >= transferIntro ? urlStep : transferIntro;
    seedFinanceTransferVisitStack(dest, transferIntro);
    markApplyReady();
    routeToVisit(peekVisit());
    return;
  }

  // 预约 / 现场开户不走线上步骤缓存续办
  const skipOnlineStepResume =
    pState.applyMethod === 'RESERVATION' || pState.applyMethod === 'ON_SITE_WITNESS';

  if (!location.query.step && !skipOnlineStepResume) {
    // editRefusedMode 下跳过终态重置，直接展示表单修正流程
    if (!pState.editRefusedMode && TERMINAL_STATUSES.includes(pState.status)) {
      clearProgressCache();
      setRecords(0);
      console.log('[OpenProgress][finishInit] terminal status → step 0', {
        status: pState.status,
        applyMethod: pState.applyMethod,
      });
    } else if (!pState.editRefusedMode) {
      const isMainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';
      const savedStep = readProgressCache(isMainland);

      if (savedStep > 1) {
        const maxStep = isMainland ? MAINLAND_MAX_RESUME_STEP : Infinity;
        const dest = Math.min(savedStep, maxStep);
        setRecords(dest);
        console.log('[OpenProgress][finishInit] resume from cache', {
          savedStep,
          dest,
          applyMethod: pState.applyMethod,
          isMainland,
        });
      } else {
        setRecords(0);
        console.log('[OpenProgress][finishInit] no cache → step 0', {
          savedStep,
          applyMethod: pState.applyMethod,
        });
      }
    }
  }

  console.log('[OpenProgress][finishInit] done', {
    current: pState.current,
    applyMethod: pState.applyMethod,
    status: pState.status,
    queryStep: location.query.step,
  });

  // 先校准 current / applyMethod，再放开 Comp，避免默认 ONLINE_TRANSFER 闪一下
  markApplyReady();

  if (!location.query.step && !skipOnlineStepResume && !pState.editRefusedMode) {
    location.routeTo(`/open-progress?step=${pState.current}`);
  }
};

const fetchAndApplyProcessData = async () => {
  // 全站单例闸门：必须先 await token，再打需登录接口（与 bootstrap 共用 Promise）
  try {
    await waitForOpenAccountBootstrap();
  } catch (e) {
    pState.loadError = true;
    pState.loadErrorMessage = e instanceof OpenAccountBootstrapTimeoutError
      ? i18n.OpenProgress.Errors.AuthExpired
      : formatLoadErrorMessage(e);
    pState.shellReady = false;
    pState.applyReady = false;
    pState.loading = false;
    return false;
  }

  // 字典不挡首屏：与 getByUserId 并行；步骤计算只依赖申请资料 / URL / 本地缓存
  void initBusConfigs().catch(() => {});

  const result = await getAllInfos();
  if (!result.ok) {
    pState.loadError = true;
    pState.loadErrorMessage = result.message;
    pState.shellReady = false;
    pState.applyReady = false;
    pState.loading = false;
    return false;
  }

  // 已跳转结果页：勿再 finishInitAfterFetch（避免又 routeTo open-progress?step=）
  if (result.blocked) {
    return true;
  }

  if (isMainlandIdCard()) {
    persistMainlandOpenAccountToken();
  }

  finishInitAfterFetch();
  return true;
};

/** 进行中复用同一 Promise，避免组件重渲染 / 重复挂载触发双拉数 */
let initPromise: Promise<void> | null = null;
let editOpinionUnsub: (() => void) | null = null;

/** App 顶栏 / 物理返回：尽早注册，勿等 fetch 完成 */
export function ensureOpenProgressAppBackHandler() {
  initRNBridge();
  if (editOpinionUnsub) return;
  editOpinionUnsub = registerRNHandler((msg) => {
    if (msg.type === 'APP_TO_WEB_EditOpinion') {
      const items = parseOpinionPayload((msg.payload as any)?.opinion);
      if (!items.length) return;
      const { errorKeys, errorValues, errorMaps } = applyOpinionErrors(items);
      pState.errorKeys = errorKeys;
      pState.errorValues = errorValues;
      pState.errorMaps = errorMaps;
      return;
    }
    // App 顶栏返回 / 物理返回：走业务 lastProcess，勿依赖 WebView 浏览器历史
    if (msg.type === 'APP_TO_WEB_GoBack') {
      // 开户文件独立页：回到开户流程，勿按入口步直接退出 App
      if (location.path === '/open-account-docs') {
        routeBackFromOpenAccountDocs(pState.current);
        return;
      }
      // 确认资料单项编辑：与底栏「返回」一致，走 editLastProcess（清 editMode）
      if (pState.editMode) {
        editLastProcess();
        return;
      }
      // 退回修改 / 财务入金续办：沿用 lastProcess（财务在入口底线见底退出）
      if (pState.editRefusedMode || pState.financeTransferResumeMode) {
        lastProcess();
        return;
      }
      // 提交最后一步（开户已申请）：回退即退出 H5，勿退回银行卡确认
      if (isOnSubmittedSuccessView()) {
        exitAfterSubmitSuccess();
        return;
      }
      // 已回到本次入口（全新=0 / 续办 / 深链续入起始步）或更靠前：直接退出到 App，
      // 不再倒退用户本次没走过的前置步骤（「上一步」按钮仍可回看更前面步骤）
      if (pState.current <= openEntryFloorStep) {
        if ((window as any).ReactNativeWebView) {
          postToApp({ type: 'WEB_TO_APP_GoBack' });
        } else {
          location.routeTo('/account');
        }
        return;
      }
      // 未触底：走本次真正访问过的页（如 9/10 → … → 5/10）
      lastProcess();
    }
  });
  if (pState.editRefusedMode && (window as any).ReactNativeWebView) {
    postToApp({
      type: 'WEB_TO_APP_OpenAccountEditOpinion_Request',
      timestamp: Date.now(),
    });
  }
}
/** primeOpenProgressShell 只跑一次，避免组件重渲染反复 setRecords */
let didPrimeShell = false;

/**
 * editRefused 冷启动：无真实路径时按 phase 种子最小访问链（见底再退退出）。
 * 不写入线性 0..n（editRefused 同 step 多 phase）。
 */
const seedEditRefusedVisitStack = (
  step: number,
  phase: VisitEntry['editRefusedPhase'],
) => {
  const n = Math.max(0, Math.floor(step));
  const chain: VisitEntry[] = [{ step: n, editRefusedPhase: 'forms' }];
  if (phase === 'risk') {
    chain.push({ step: n, editRefusedPhase: 'risk' });
  } else if (phase === 'video') {
    chain.push({ step: n, editRefusedPhase: 'video' });
  } else if (phase === 'sign' || phase === 'done') {
    // 深链不知是否经 risk，兜底 forms → 当前 phase（返回先回 forms）
    chain.push({ step: n, editRefusedPhase: phase });
  }
  visitStack = chain;
  applyVisit(peekVisit(), { notify: false, completePrev: false, route: false });
};

/**
 * 财务回退入金：栈仅含入金区间（底线=TransferIntro），勿 seed 0..n，避免「上一步」退到签名/资料。
 */
const seedFinanceTransferVisitStack = (destStep: number, floorStep: number) => {
  const floor = Math.max(0, Math.floor(floorStep));
  const dest = Math.max(floor, Math.floor(destStep));
  openEntryFloorStep = floor;
  if (dest === floor) {
    visitStack = [{ step: floor }];
  } else {
    // 深链落在入金中后段：最小链 floor → dest，返回可回到入金首页
    visitStack = [{ step: floor }, { step: dest }];
  }
  applyVisit(peekVisit(), { notify: false, completePrev: false, route: false });
};

/**
 * 同步：按 URL 挂开户壳（不等 token / getByUserId）。
 * 供 OpenProcess 首帧与 initProcess 共用，保证 FCP 早于接口。
 */
export const primeOpenProgressShell = () => {
  if (didPrimeShell) return;

  if (location.query.mode === 'backReopen') {
    pState.backReopenMode = true;
  }

  const stepRaw = location.query.step;
  const step = stepRaw != null && stepRaw !== '' ? +stepRaw : NaN;
  const hasStep = !Number.isNaN(step);
  const role = String(location.query.role || '');
  const backType = String(location.query.backType || '');

  if (location.query.mode === 'editRefused') {
    // 财务 / fundBack 入金回退：不进纠错壳，首帧先挂入金壳（证件类型未知时暂用大陆 17，fetch 后校正）
    if (isFinanceTransferEditRole(role, backType)) {
      pState.financeTransferResumeMode = true;
      pState.financeTransferResumeRole = role || 'Activity_admin_11';
      pState.editRefusedMode = false;
      const provisionalFloor = 17;
      const provisionalDest = hasStep && step >= 16 ? step : provisionalFloor;
      seedFinanceTransferVisitStack(provisionalDest, provisionalFloor);
      pState.shellReady = true;
      didPrimeShell = true;
      return;
    }

    pState.editRefusedMode = true;
    const phaseQ = location.query.phase;
    let phase: VisitEntry['editRefusedPhase'] = 'forms';
    if (phaseQ === 'sign') phase = 'sign';
    else if (phaseQ === 'risk') phase = 'risk';
    else if (phaseQ === 'done') phase = 'done';
    else if (phaseQ === 'video') phase = 'video';
    pState.editRefusedPhase = phase;
    if (phase === 'forms') {
      pState.editRefusedFormStep = 0;
    }
    seedEditRefusedVisitStack(hasStep ? step : 0, phase);
    pState.shellReady = true;
    didPrimeShell = true;
    return;
  }

  if (hasStep) {
    setRecords(step);
    pState.shellReady = true;
    didPrimeShell = true;
  }
};

export const initProcess = async () => {
  if (initPromise) return initPromise;
  if (!pState.loading) return;

  primeOpenProgressShell();

  initPromise = (async () => {
    try {
      if (pState.editRefusedMode) {
        preloadEditRefusedSteps();
      }
      if (pState.financeTransferResumeMode) {
        preloadDeepLinkShell(pState.current || 17);
      } else if (location.query.step) {
        preloadDeepLinkShell(+location.query.step);
      }

      // 尽早初始化 RNBridge + App 返回
      ensureOpenProgressAppBackHandler();

      await fetchAndApplyProcessData();
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
};

/** 接口报错或 401 / token 超时时由页面触发，重新拉取开户数据 */
export const retryInitProcess = async () => {
  pState.loadError = false;
  pState.loadErrorMessage = '';
  pState.loading = true;
  pState.applyReady = false;
  // 深链 / 已在壳上：重试时保持骨架标记，业务 Comp 仍等 applyReady
  if (location.query.step || pState.editRefusedMode || pState.financeTransferResumeMode || pState.current > 0) {
    pState.shellReady = true;
  }
  initPromise = null;
  await fetchAndApplyProcessData();
};

/** 单类型单文件：有则改，无则增；url 为空则移除该类型全部 */
export const changeFile = (type, url) => {
  if (!url) {
    pState.attachmentList = pState.attachmentList.filter((i) => i.attachType !== type);
    return;
  }
  const item = pState.attachmentList.find((i) => i.attachType === type);
  if (item) {
    item.fileUrl = url;
  } else {
    pState.attachmentList.push({
      busId: pState.id,
      attachType: type,
      fileUrl: url,
    });
  }
};

/** 追加同类型附件（多图） */
export const addAttachmentFile = (type: string, url: string) => {
  if (!url) return;
  pState.attachmentList.push({
    busId: pState.id,
    attachType: type,
    fileUrl: url,
  });
};

/** 按 url 删除同类型附件 */
export const removeAttachmentFile = (type: string, url: string) => {
  if (!url) return;
  pState.attachmentList = pState.attachmentList.filter(
    (i) => !(i.attachType === type && i.fileUrl === url),
  );
};

/** 后续 apply/save 统一附带交易权限，避免漏传 */
export const getApplySaveExtra = () => ({
  needDerivativeTradePermission: !!pState.selectAccountForm.needDerivativeTradePermission,
});
